import { CUSTOM_COLOR_ID, findPreset } from '../model/colors.ts';
import type { LightLayout, LightStyle, Run, TimePreset } from '../model/types.ts';
import type { Fixture } from './prompts.ts';
import type { PromptOptions } from './protocol.ts';
import { buildAiRequestOptions } from './request.ts';

/**
 * AI render looks (docs/spec/10-ai-render-looks.md): a look is a color/theme plus a fixture
 * style; a batch renders every selected look at every selected time of day.
 */
export interface Look {
  /** COLOR_PRESETS id or "custom". */
  colorId: string;
  /** #RRGGBB, used when colorId is "custom". */
  customHex: string;
  fixture: Fixture;
}

/** Per fixture: labels and the spacing / layout hint sent instead of the project's (null = the project's). */
export const FIXTURE_DEFAULTS: Record<Fixture, { label: string; shortLabel: string; spacingIn: number | null; layout: LightLayout | null }> = {
  permanent: { label: 'Permanent LED', shortLabel: 'Permanent', spacingIn: null, layout: null },
  c9: { label: 'Christmas bulbs (C9)', shortLabel: 'C9 bulbs', spacingIn: 12, layout: 'single' },
  mini: { label: 'Mini lights', shortLabel: 'Mini lights', spacingIn: 4, layout: 'single' },
};

export const MAX_RENDERS_PER_BATCH = 6;

const TIME_LABELS: Record<TimePreset, string> = { day: 'Day', evening: 'Evening', night: 'Night' };

/** Chip label "<Color name> · <fixture short label>", e.g. "Warm White · Permanent", "Christmas · C9 bulbs". */
export function lookLabel(look: Look): string {
  return `${findPreset(look.colorId, look.customHex).name} · ${FIXTURE_DEFAULTS[look.fixture].shortLabel}`;
}

/** Result label "<Time> · <Color name> · <fixture short label>"; just "<Time>" for results without a look. */
export function renderLabel(time: TimePreset, look?: Look | null): string {
  return look ? `${TIME_LABELS[time]} · ${lookLabel(look)}` : TIME_LABELS[time];
}

/** Two looks are the same selection (customHex only matters for "custom", compared case-insensitively). */
export function sameLook(a: Look, b: Look): boolean {
  return (
    a.colorId === b.colorId &&
    a.fixture === b.fixture &&
    (a.colorId !== CUSTOM_COLOR_ID || a.customHex.toLowerCase() === b.customHex.toLowerCase())
  );
}

/** Default selection: the project's current color with permanent lights. */
export function defaultLook(colorId: string, customHex: string): Look {
  return { colorId, customHex, fixture: 'permanent' };
}

/**
 * Quick-add chips. "Multicolor" is the Rainbow preset (the spec names no "Multicolor" preset).
 * The UI hides chips whose look is already selected (sameLook).
 */
export const QUICK_LOOKS: readonly Look[] = [
  { colorId: 'warm-2700', customHex: '#FF5A1F', fixture: 'permanent' },
  { colorId: 'christmas', customHex: '#FF5A1F', fixture: 'c9' },
  { colorId: 'warm-2700', customHex: '#FF5A1F', fixture: 'c9' },
  { colorId: 'rainbow', customHex: '#FF5A1F', fixture: 'mini' },
];

/** Wire options for one look: the look's color and fixture; c9 / mini replace spacing and layout with their defaults. */
export function lookOptions(look: Look, project: { runs: Run[]; style: LightStyle; windowGlow: boolean }): PromptOptions {
  const d = FIXTURE_DEFAULTS[look.fixture];
  const base = buildAiRequestOptions({ runs: project.runs, style: project.style, colorId: look.colorId, customHex: look.customHex, windowGlow: project.windowGlow });
  return {
    ...base,
    ...(d.spacingIn != null ? { spacingIn: d.spacingIn } : {}),
    ...(d.layout != null ? { layout: d.layout } : {}),
    fixture: look.fixture,
  };
}

export interface RenderBatch {
  /** One render per (look × time), looks outer, times inner, in selection order. */
  renders: { look: Look; time: TimePreset; options: PromptOptions }[];
  count: number;
  /** count × creditsPerImage. */
  credits: number;
  /** Generate is allowed only when 0 < count ≤ max (balance is checked separately). */
  withinLimit: boolean;
  /** "2 looks × 3 times = 6 renders · 6 credits". */
  summary: string;
}

/** The batch the AI tab would send: every look × every time (max 6 renders, credits per image from /v1/me). */
export function renderBatch(
  looks: Look[],
  times: TimePreset[],
  project: { runs: Run[]; style: LightStyle; windowGlow: boolean },
  opts: { max?: number; creditsPerImage?: number } = {},
): RenderBatch {
  const max = opts.max ?? MAX_RENDERS_PER_BATCH;
  const per = opts.creditsPerImage ?? 1;
  const renders = looks.flatMap((look) => times.map((time) => ({ look, time, options: lookOptions(look, project) })));
  const count = renders.length;
  const credits = count * per;
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  return {
    renders,
    count,
    credits,
    withinLimit: count > 0 && count <= max,
    summary: `${plural(looks.length, 'look', 'looks')} × ${plural(times.length, 'time', 'times')} = ${plural(count, 'render', 'renders')} · ${plural(credits, 'credit', 'credits')}`,
  };
}
